import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentsList } from "../components/workspace/DocumentsList";
import { toast } from "sonner";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ChatCircleDots, Folders, PencilSimple, Sparkle, Stack, Plus, BookOpen, SquaresFour, Trash, UploadSimple, ShieldCheck, Users, Files } from "@phosphor-icons/react";
import PolicyAssignments from "../components/PolicyAssignments";
import PolicyAuthoring from "../components/PolicyAuthoring";
import PolicyBooks from "../components/PolicyBooks";
import LibrarySetup from "../components/LibrarySetup";
import PolicyCleanup from "../components/PolicyCleanup";
import PolicyImport from "../components/PolicyImport";
import PolicyAI from "../components/PolicyAI";
import PolicyAudit from "../components/PolicyAudit";
import PolicyClassify from "../components/PolicyClassify";
import PolicyRamsUpload from "../components/PolicyRamsUpload";
import WorkAssignments from "../components/WorkAssignments";
import { useAuth } from "../context/AuthContext";

function Placeholder({ title, note }) {
  return (
    <div data-testid="phase-placeholder" className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <h3 className="font-display text-lg font-bold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-lg mx-auto">{note}</p>
      <span className="inline-block mt-3 text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">Coming in a later phase</span>
    </div>
  );
}

function FeedbackQueue() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const load = () => api.get(`/policies/feedback${status ? `?status=${status}` : ""}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const send = async (newStatus) => {
    try { await api.post(`/policies/feedback/${active.id}/reply`, { reply, status: newStatus }); toast.success("Saved"); setActive(null); setReply(""); load(); }
    catch { toast.error("Could not save"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["open", "replied", "accepted", "closed", ""].map((s) => (
          <button key={s || "all"} data-testid={`fbq-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`text-sm font-semibold px-3 py-1.5 rounded-full border ${status === s ? "bg-emerald-700 text-white border-emerald-700" : "border-slate-200 text-slate-600"}`}>{s || "All"}</button>
        ))}
      </div>
      <div className="flex flex-col gap-2" data-testid="feedback-queue">
        {rows.length === 0 && <p className="text-slate-400 text-sm py-8 text-center">No feedback in this state.</p>}
        {rows.map((f) => (
          <button key={f.id} data-testid={`fbq-${f.id}`} onClick={() => { setActive(f); setReply(f.reply || ""); }}
            className="text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-emerald-300">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-slate-800 text-sm truncate">{f.document?.title || "Document"}</span>
              <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${f.status === "open" ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{f.status}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{f.kind.replace(/_/g, " ")}{f.mode ? ` · ${f.mode === "need_clarification" ? "needs clarification" : "suggestion"}` : ""} · {f.person?.name}</div>
            <p className="text-sm text-slate-700 mt-1 line-clamp-2">{f.body}</p>
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="fbq-dialog">
          <DialogHeader><DialogTitle>Respond to feedback</DialogTitle></DialogHeader>
          {active && (<>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="text-xs font-bold uppercase text-slate-500">{active.kind.replace(/_/g, " ")} · {active.person?.name}</div>
              <p className="text-slate-700 mt-1">{active.body}</p>
            </div>
            <Textarea data-testid="fbq-reply" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" className="min-h-[100px]" />
            <p className="text-xs text-slate-400">Your reply notifies the submitter. Feedback does not grant an exemption or authorise alternative working.</p>
          </>)}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => send("info_requested")}>Request info</Button>
            <Button variant="outline" onClick={() => send("accepted")}>Accept for revision</Button>
            <Button variant="outline" onClick={() => send("closed")}>Close</Button>
            <Button data-testid="fbq-send" onClick={() => send("replied")} className="bg-emerald-700 hover:bg-emerald-800">Reply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionsManager() {
  const [sections, setSections] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/policies/sections").then((r) => setSections(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name.trim()) return; await api.post("/policies/sections", { name, sort_order: sections.length }); setName(""); load(); toast.success("Section added"); };
  return (
    <div className="max-w-xl">
      <div className="flex gap-2 mb-4">
        <Input data-testid="section-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New section name" className="bg-white" />
        <Button data-testid="section-add" onClick={add} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><Plus size={16} /> Add</Button>
      </div>
      <div className="flex flex-col gap-2" data-testid="sections-manager">
        {sections.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
            <span className="font-medium text-slate-800 text-sm">{s.name}</span>
            <span className="font-mono text-xs text-slate-400">#{s.sort_order}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-3">Reordering, subsections, rename and archive are available here; drag-reorder UI lands with the authoring editor in P2C.</p>
    </div>
  );
}

function NewDocDialog({ onClose }) {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("policy");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const create = async () => {
    if (busy) return; setBusy(true);
    try { const r = await api.post("/policies/authoring", { title, kind }); nav(`/policy-manager/doc/${r.data.document.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ws-new-doc-dialog">
        <DialogHeader><DialogTitle>New document</DialogTitle></DialogHeader>
        <Input data-testid="ws-new-doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
        <select data-testid="ws-new-doc-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm">
          {["policy", "procedure", "statement", "guidance", "privacy", "toolbox_talk"].map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
        </select>
        <p className="text-xs text-slate-500">Creates an unassigned draft. For Word/PDF import or AI drafting, use the Authoring tab.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ws-new-doc-create" disabled={busy || !title.trim()} onClick={create} className="bg-emerald-700 hover:bg-emerald-800">Create &amp; open</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PolicyManagerPage() {
  const { isSuperAdmin } = useAuth();
  const [tab, setTab] = useState("documents");
  const [newOpen, setNewOpen] = useState(false);
  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Policy Manager</h1>
        <p className="font-body text-slate-600 mt-1 text-sm">Open any document to edit its content, details, booklets, audience and revisions in one place.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-nowrap overflow-x-auto whitespace-nowrap md:flex-wrap h-auto max-w-full justify-start">
          <TabsTrigger value="documents" data-testid="tab-documents" className="gap-1.5"><Files size={15} /> Documents</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback" className="gap-1.5"><ChatCircleDots size={15} /> Feedback</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library" className="gap-1.5"><SquaresFour size={15} /> Library setup</TabsTrigger>
          <TabsTrigger value="books" data-testid="tab-books" className="gap-1.5"><BookOpen size={15} /> Books</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections" className="gap-1.5"><Folders size={15} /> Sections</TabsTrigger>
          <TabsTrigger value="classify" data-testid="tab-classify" className="gap-1.5"><Folders size={15} /> Classify</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments" className="gap-1.5"><Stack size={15} /> Assignments</TabsTrigger>
          <TabsTrigger value="authoring" data-testid="tab-authoring" className="gap-1.5"><PencilSimple size={15} /> Authoring</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import" className="gap-1.5"><UploadSimple size={15} /> Import</TabsTrigger>
          <TabsTrigger value="rams-upload" data-testid="tab-rams-upload" className="gap-1.5"><UploadSimple size={15} /> RAMS / MS</TabsTrigger>
          <TabsTrigger value="workforce" data-testid="tab-workforce" className="gap-1.5"><Users size={15} /> Client/Job workforce</TabsTrigger>
          <TabsTrigger value="cleanup" data-testid="tab-cleanup" className="gap-1.5"><Trash size={15} /> Cleanup</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai" className="gap-1.5"><Sparkle size={15} /> AI workspace</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="audit" data-testid="tab-audit" className="gap-1.5"><ShieldCheck size={15} /> Audit</TabsTrigger>}
        </TabsList>
        <TabsContent value="documents" className="mt-5"><DocumentsList onCreate={() => setNewOpen(true)} onUploadPdf={() => setTab("rams-upload")} /></TabsContent>
        <TabsContent value="feedback" className="mt-5"><FeedbackQueue /></TabsContent>
        <TabsContent value="library" className="mt-5"><LibrarySetup /></TabsContent>
        <TabsContent value="books" className="mt-5"><PolicyBooks /></TabsContent>
        <TabsContent value="sections" className="mt-5"><SectionsManager /></TabsContent>
        <TabsContent value="classify" className="mt-5"><PolicyClassify /></TabsContent>
        <TabsContent value="assignments" className="mt-5"><PolicyAssignments /></TabsContent>
        <TabsContent value="authoring" className="mt-5"><PolicyAuthoring /></TabsContent>
        <TabsContent value="import" className="mt-5"><PolicyImport /></TabsContent>
        <TabsContent value="rams-upload" className="mt-5"><PolicyRamsUpload /></TabsContent>
        <TabsContent value="workforce" className="mt-5"><WorkAssignments /></TabsContent>
        <TabsContent value="cleanup" className="mt-5"><PolicyCleanup /></TabsContent>
        <TabsContent value="ai" className="mt-5"><PolicyAI /></TabsContent>
        {isSuperAdmin && <TabsContent value="audit" className="mt-5"><PolicyAudit /></TabsContent>}
      </Tabs>
      {newOpen && <NewDocDialog onClose={() => setNewOpen(false)} />}
    </div>
  );
}
